/**
 * ShareModal - Modal for sharing event/membership pages
 *
 * Displays share options for social media, email, and clipboard.
 */
import { useTranslation } from "@shared/utils/internationalization";
import { useClipboard } from "@shared/hooks/useClipboard";
import ResponsiveDialog from "@ui-kit/overlays/ResponsiveDialog/ResponsiveDialog.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Button from "@ui-kit/form/Button/Button.web";
import SimpleGrid from "@ui-kit/layout/Grid/SimpleGrid.web";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import Icon from "@ui-kit/icons/Icon.web";

export interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  organizer: string;
  image: string;
  url: string;
}

export function ShareModal({
  isOpen,
  onClose,
  title,
  subtitle,
  organizer,
  image,
  url,
}: ShareModalProps) {
  const { t } = useTranslation("common");
  const { onCopy, hasCopied } = useClipboard(url);

  const shareText = `${title}${subtitle ? ` - ${subtitle}` : ""}`;

  const handleTwitterShare = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(shareText)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleFacebookShare = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(shareText);
    const body = encodeURIComponent(`${shareText}\n\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      headerComponent={
        <Text fontWeight="semibold" fontSize="xl">
          {t("Share", { ns: "purchases" })}
        </Text>
      }
      bodyComponent={
        <Flex direction="column" gap="lg">
          {/* Event Preview */}
          <Flex gap="md" alignItems="flex-start">
            <Box
              flexShrink={0}
              width="element.8xl"
              height="element.5xl"
              borderRadius="md"
              overflow="hidden"
            >
              <CardImage
                src={image}
                alt={title}
                borderRadius="md"
                width={120}
                height={64}
              />
            </Box>
            <Flex direction="column" gap="2xs" flex={1} minWidth={0}>
              <Text
                color="fg"
                fontWeight="semibold"
                fontSize="lg"
                lineClamp={2}
              >
                {title}
              </Text>
              {subtitle && (
                <Text color="fg" fontSize="sm">
                  {subtitle}
                </Text>
              )}
              <Text color="fg.subtle" fontSize="sm">
                {organizer}
              </Text>
            </Flex>
          </Flex>

          {/* Share Buttons */}
          <SimpleGrid columns={2} gap="md">
            <Button.Outline
              onClick={handleTwitterShare}
              justifyContent="center"
              py="md"
              data-testid="share-twitter-button"
              color="fg"
            >
              <Icon.XLogo boxSize="element.lg" mr="xs" color="inherit" />X
            </Button.Outline>

            <Button.Outline
              onClick={handleFacebookShare}
              justifyContent="center"
              py="md"
              data-testid="share-facebook-button"
              color="fg"
            >
              <Icon.FacebookLogo boxSize="element.lg" mr="xs" color="inherit" />
              {t("Facebook")}
            </Button.Outline>

            <Button.Outline
              onClick={handleEmailShare}
              justifyContent="center"
              py="md"
              data-testid="share-email-button"
              color="fg"
            >
              <Icon.Envelope.Outline
                boxSize="element.lg"
                mr="xs"
                color="inherit"
              />
              {t("Email")}
            </Button.Outline>

            <Button.Outline
              onClick={onCopy}
              justifyContent="center"
              py="md"
              data-testid="share-copy-button"
              color="fg"
            >
              {hasCopied ? (
                <>
                  <Icon.Checkmark.Solid
                    boxSize="element.lg"
                    mr="xs"
                    color="inherit"
                  />
                  {t("Copied!")}
                </>
              ) : (
                <>
                  <Icon.Copy.Outline
                    boxSize="element.lg"
                    mr="xs"
                    color="inherit"
                  />
                  {t("Copy link")}
                </>
              )}
            </Button.Outline>
          </SimpleGrid>
        </Flex>
      }
    />
  );
}
